import clsx from 'clsx'
import {getIn, useFormik} from 'formik'
import {compact, first, get, last, omit} from 'lodash'
import moment from 'moment'
import React, {useCallback, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../../_gori/hooks/UseYupValidate'
import {
  Button,
  InputDate,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import {useA1BatchesProvider} from '../../../../../_gori/providers/A1BatchesProvider'
import {FromOrToModal, FromOrToModalData} from '../../../orders'
import A1_CONSTANTS from '../../core/_const'
import A1BatchService from '../../core/_requests'
import {A1ServiceModal} from './A1ServiceModal'
import {InputClickEdit} from '../../../../../_gori/partials/widgets/form/InputClickEdit'
import {FORMAT_DATE} from '../../../../../_gori/constants'
import {formatAMPM} from '../../../../../_gori/utils'
import {useAuth} from '../../../auth'

type Props = {
  show: boolean
  handleClose: () => void
  handleSuccess: () => void
}

const JourneyInformationModal: React.FC<Props> = ({show, handleClose, handleSuccess}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {orderYup, stringYup, numberYup, regexYup, dateYup, infoYup} = UseYupValidate()
  const {a1Batch} = useA1BatchesProvider()

  const [validationErrors, setValidationErrors] = useState<any>()
  const [loading, setLoading] = useState<{update: boolean}>({update: false})
  const [hasShow, setHasShow] = useState<{a1Service: boolean}>({a1Service: false})

  const [fromToModal, setFormToModal] = useState<FromOrToModalData>({
    show: false,
    name: 'pickup_address',
    data: {},
    validate: {},
  })
  const {currentUser} = useAuth()

  const handleCloseFromToModal = () => {
    setFormToModal({
      show: false,
      name: 'pickup_address',
      data: {},
      validate: {},
    })
  }

  const handleSaveModal = (name, values) => {
    formik.setFieldValue(name, values)
    handleCloseFromToModal()
  }

  const handleCloseModal = () => {
    handleClose()
  }

  const formToModalSubLabel = useMemo(() => {
    if (fromToModal.name === 'pickup_address') return 'PICK_UP_ADDRESS'
    if (fromToModal.name === 'ship_from') return 'SHIP_FROM'

    return ''
  }, [fromToModal.name])

  const isEditableShipFrom = useMemo(
    () => !a1Batch?.order_partially_done,
    [a1Batch?.order_partially_done]
  )

  const validateAddress = useCallback(
    (name) => {
      const validate = {
        [`${name}_company`]: Yup.string().when([`${name}_first_name`], {
          is: (first_name) => !first_name || first_name.trim() === '',
          then: orderYup.companyFirstName('COMPANY'),
          otherwise: stringYup(255, 'COMPANY', false),
        }),
        [`${name}_first_name`]: Yup.string().when(`${name}_company`, {
          is: (company) => !company || company.trim() === '',
          then: orderYup.companyFirstName('FIRST_NAME'),
          otherwise: stringYup(255, 'FIRST_NAME', false),
        }),
        [`${name}_street1`]: stringYup(255, 'ADDRESS'),
        [`${name}_city`]: stringYup(255, 'CITY'),
        [`${name}_state`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.state(),
          otherwise: stringYup(255, 'STATE'),
        }),
        [`${name}_country`]: regexYup.country(),
        [`${name}_zip`]: Yup.string().when(`${name}_country`, {
          is: (val) => val === 'US',
          then: regexYup.zipCode(),
          otherwise: stringYup(255, 'POSTAL_CODE'),
        }),
        [`${name}_last_name`]: stringYup(255, 'LAST_NAME', false),
        [`${name}_street2`]: stringYup(255, 'ADDRESS_2', false),
        [`${name}_phone`]: infoYup.phone('PHONE', false),
        [`${name}_email`]: infoYup.email('EMAIL', false),
      }
      return validate
    },
    [orderYup, regexYup, stringYup, infoYup]
  )

  const initValidateSchema = useMemo(() => {
    const pickUpDate: any = {
      pickup_address: Yup.object().shape(validateAddress('pickup_address'), [
        ['pickup_address_first_name', 'pickup_address_company'],
      ]),
      estimated_pickup_at: dateYup(
        'PICK_UP_DATE',
        moment().local().add(1, 'hour'),
        undefined,
        undefined,
        true,
        'PICK_UP_TIME_MUST_BE_1H_ONWARDS'
      ),
    }
    const routeAndService: any = {
      from: stringYup(255, 'FROM'),
      to: stringYup(255, 'TO'),
      services: stringYup(255, 'A1_SERVICE'),
    }

    let schema: any = {
      pallets: numberYup.journey_info({
        name: 'PALLETS',
        max: {value: 10000, label: '10,000'},
      }),
      parcels: numberYup.journey_info({
        name: 'PARCELS',
        max: {value: 10000, label: '10,000'},
      }),
      weight: numberYup.journey_info({
        name: 'KGS',
        integer: false,
        min: {value: 0, label: '0'},
        max: {value: 1000000, label: '1,000,000'},
        moreThan: true,
      }),
    }
    if (isEditableShipFrom) {
      schema.ship_from = Yup.object().shape(validateAddress('ship_from'), [
        ['ship_from_first_name', 'ship_from_company'],
      ])
    }

    if (!currentUser?.settings?.yamato_account) {
      schema = {
        ...pickUpDate,
        ...routeAndService,
        ...schema,
      }
    }
    return schema
  }, [dateYup, numberYup, stringYup, validateAddress, isEditableShipFrom, currentUser])

  const initialValues = useMemo(() => {
    return {
      from: get(first(a1Batch?.flights), 'origin_iata') || null,
      to: get(last(a1Batch?.flights), 'destination_iata') || null,
      pickup_address: {
        pickup_address_company: a1Batch?.pickup_address?.company || null,
        pickup_address_first_name: a1Batch?.pickup_address?.first_name || null,
        pickup_address_last_name: a1Batch?.pickup_address?.last_name || null,
        pickup_address_street1: a1Batch?.pickup_address?.street1 || null,
        pickup_address_street2: a1Batch?.pickup_address?.street2 || null,
        pickup_address_city: a1Batch?.pickup_address?.city || null,
        pickup_address_state: a1Batch?.pickup_address?.state || null,
        pickup_address_zip: a1Batch?.pickup_address?.zip || null,
        pickup_address_country: a1Batch?.pickup_address?.country || null,
        pickup_address_phone: a1Batch?.pickup_address?.phone || null,
        pickup_address_email: a1Batch?.pickup_address?.email || null,
        pickup_address_is_residential: false,
      },
      estimated_pickup_at: a1Batch?.estimated_pickup_at || moment(),
      services: 'all_in_one',
      pallets: a1Batch?.origin?.packaging?.pallets || null,
      parcels: a1Batch?.origin?.packaging?.parcels || null,
      weight: parseFloat(a1Batch?.origin?.packaging?.weight?.value) || null,
      ship_from: {
        ship_from_company: a1Batch?.from?.company || null,
        ship_from_first_name: a1Batch?.from?.first_name || null,
        ship_from_last_name: a1Batch?.from?.last_name || null,
        ship_from_street1: a1Batch?.from?.street_1 || null,
        ship_from_street2: a1Batch?.from?.street_2 || null,
        ship_from_city: a1Batch?.from?.city || null,
        ship_from_state: a1Batch?.from?.state || null,
        ship_from_zip: a1Batch?.from?.zip || null,
        ship_from_country: a1Batch?.from?.country || null,
        ship_from_phone: a1Batch?.from?.phone || null,
        ship_from_email: a1Batch?.from?.email || null,
      },
    }
  }, [
    a1Batch?.flights,
    a1Batch?.pickup_address,
    a1Batch?.estimated_pickup_at,
    a1Batch?.origin?.packaging?.pallets,
    a1Batch?.origin?.packaging?.parcels,
    a1Batch?.origin?.packaging?.weight?.value,
    a1Batch?.from,
  ])

  const formik = useFormik({
    initialValues,
    enableReinitialize: false,
    validationSchema: Yup.object().shape(initValidateSchema),
    onSubmit: async (values) => {
      const config = {cancelToken: newCancelToken()}

      try {
        setLoading((prev) => ({...prev, update: true}))
        const res = await A1BatchService.updateA1Batch(
          a1Batch.id,
          {
            ...omit(values, 'estimated_pickup_at'),
            pickup_address: Object?.entries(values?.pickup_address)?.reduce(
              (address, [key, value]) => {
                address[`${key?.split('pickup_address_')[1]}`] = value
                return address
              },
              {}
            ),
            ship_from: Object?.entries(values?.ship_from)?.reduce((address, [key, value]) => {
              address[`${key?.split('ship_from_')[1]}`] = value
              return address
            }, {}),
            epu_at: !currentUser?.settings?.yamato
              ? formatAMPM(
                  moment(values.estimated_pickup_at)
                    .local()
                    .format(FORMAT_DATE.DATE_TIME_TIMEZONE_FULL)
                )
              : null,
          },
          config
        )
        if (res) {
          handleSuccess()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setLoading((prev) => ({...prev, update: false}))
      }
    },
  })

  // BEGIN: Pickup Address
  const shipPickUpFromShow = useMemo(() => {
    const {pickup_address_city, pickup_address_country} = formik.values.pickup_address
    const addressTo = compact([pickup_address_city, pickup_address_country]).join(', ')
    return addressTo
  }, [formik.values.pickup_address])

  const checkValidateFailed = useMemo(() => {
    if (
      !formik.values.pickup_address.pickup_address_company &&
      !formik.values.pickup_address.pickup_address_first_name
    ) {
      return true
    }
    const validate = [
      'pickup_address_city',
      'pickup_address_state',
      'pickup_address_country',
      'pickup_address_zip',
      'pickup_address_street1',
    ]
    return validate.some((item) => !formik.values.pickup_address[item])
  }, [formik.values])

  // END: Pickup Address

  // BEGIN: Ship From Address
  const shipFromShow = useMemo(() => {
    const {ship_from_city, ship_from_country} = formik.values.ship_from
    const addressTo = compact([ship_from_city, ship_from_country]).join(', ')
    return addressTo
  }, [formik.values.ship_from])

  const checkShipFromValidateFailed = useMemo(() => {
    if (!isEditableShipFrom) return false

    if (
      !formik.values.ship_from.ship_from_company &&
      !formik.values.ship_from.ship_from_first_name
    ) {
      return true
    }
    const validate = [
      'ship_from_city',
      'ship_from_state',
      'ship_from_country',
      'ship_from_zip',
      'ship_from_street1',
    ]

    return validate.some((item) => !formik.values.ship_from[item])
  }, [formik.values.ship_from, isEditableShipFrom])
  // END: Ship From Address

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      {hasShow.a1Service && (
        <A1ServiceModal
          show={show}
          handleClose={() => {
            setHasShow((prev) => ({...prev, a1Service: false}))
          }}
        />
      )}
      {fromToModal.show && (
        <FromOrToModal
          data={fromToModal}
          handleSave={handleSaveModal}
          handleClose={handleCloseFromToModal}
          labelModal={'JOURNEY_INFORMATION'}
          hasBack
          subLabelModal={formToModalSubLabel}
        />
      )}
      <Modal
        id='gori_modal_journey_information_modal'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-xl h-auto'
        contentClassName='modal-padding'
        show={show && !fromToModal.show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'JOURNEY_INFORMATION'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={clsx('vh-75 overflow-auto', {'cursor-no-drop': loading.update})}>
          <div
            className={clsx('journey-information-modal', {'pe-none opacity-75': loading.update})}
          >
            {!currentUser?.settings?.yamato_account && (
              <div className='journey-info__section border-bottom'>
                <h3 className='required'> {intl.formatMessage({id: 'PICK_UP_INFORMATION'})}</h3>
                <div className='journey-info__row journey-info__row--pickup'>
                  <div className='journey-info__field'>
                    <div className='journey-info__field-inner d-flex w-100 align-items-start'>
                      <InputDate
                        className='journey-info__input'
                        label={intl.formatMessage({id: 'PICK_UP_DATE'})}
                        labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                        name='estimated_pickup_at'
                        formik={formik}
                        min={moment()}
                        views={['year', 'month', 'day', 'hours', 'minutes']}
                        isLocalTime
                        dateTimeProps={{
                          ampm: true,
                          format: FORMAT_DATE.DATE_TIME,
                        }}
                      />
                    </div>
                  </div>
                  <div className='journey-info__field'>
                    <div className='journey-info__field-inner d-flex w-100'>
                      <label className='journey-info__label col-form-label fs-5 fw-bolder text-dark'>
                        {intl.formatMessage({id: 'PICK_UP_ADDRESS'})}
                      </label>
                      <div className='input-custom journey-info__input'>
                        <InputClickEdit
                          value={shipPickUpFromShow}
                          isInvalid={getIn(formik.touched, 'pickup_address') && checkValidateFailed}
                          isValid={getIn(formik.touched, 'pickup_address') && !checkValidateFailed}
                          placeholder={intl.formatMessage({id: 'ADD_PICK_UP_ADDRESS'})}
                          onClick={() => {
                            formik.setFieldTouched('pickup_address', true)
                            setFormToModal({
                              show: true,
                              name: 'pickup_address',
                              data: formik.getFieldProps('pickup_address').value,
                              validate: validateAddress('pickup_address'),
                            })
                          }}
                        />
                      </div>
                    </div>
                    {checkValidateFailed && getIn(formik.touched, 'pickup_address') && (
                      <div className='journey-info__field-error'>
                        <span className='text-danger fs-7'>
                          {intl.formatMessage(
                            {id: 'REQUIRED'},
                            {input: intl.formatMessage({id: 'PICK_UP_ADDRESS'})}
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!currentUser?.settings?.yamato_account && (
              <div className='journey-info__section border-bottom'>
                <h3 className={clsx({required: !currentUser?.settings?.yamato_account})}>
                  {intl.formatMessage({id: 'ROUTE_AND_SERVICE'})}
                </h3>
                <div className='journey-info__row journey-info__row--route'>
                  <div className='journey-info__field journey-info__field--from'>
                    <div className='journey-info__field-inner d-flex w-100'>
                      <SelectFormik
                        className='journey-info__input'
                        labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                        label={intl.formatMessage({id: 'DEPART_FROM'})}
                        placeholder={intl.formatMessage({id: 'ORIGIN'})}
                        options={A1_CONSTANTS.AIRPORT_CODES}
                        formik={formik}
                        name='from'
                        emptyDefault={false}
                        disabled={currentUser?.settings?.yamato_account}
                      />
                    </div>
                  </div>
                  <div className='journey-info__field journey-info__field--to'>
                    <div className='journey-info__field-inner d-flex w-100'>
                      <SelectFormik
                        className='journey-info__input'
                        labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                        label={intl.formatMessage({id: 'ARRIVE_TO'})}
                        placeholder={intl.formatMessage({id: 'DESTINATION'})}
                        options={A1_CONSTANTS.AIRPORT_CODES}
                        formik={formik}
                        name='to'
                        emptyDefault={false}
                        disabled={currentUser?.settings?.yamato_account}
                      />
                    </div>
                  </div>
                  <div className='journey-info__field journey-info__field--service'>
                    <div className='journey-info__field-inner d-flex w-100'>
                      <SelectFormik
                        className='journey-info__input'
                        labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                        label={intl.formatMessage({id: 'A1_SERVICE'})}
                        placeholder={intl.formatMessage({id: 'SELECT'})}
                        options={Object.values(A1_CONSTANTS.OPTIONS_A1_SERVICE)}
                        formik={formik}
                        name='services'
                        emptyDefault={false}
                        onChange={(selected) => {
                          if (selected.value === A1_CONSTANTS.OPTIONS_A1_SERVICE.ALL_IN_ONE.value) {
                            setHasShow((prev) => ({...prev, a1Service: true}))
                          }
                        }}
                        hasUseIntl={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className='journey-info__section'>
              <h3>{intl.formatMessage({id: 'LAST_MILE_SHIPMENT_DETAIL'})}</h3>
              <div className='journey-info__row journey-info__row--last-mile'>
                <div className='journey-info__field journey-info__field--ship-from'>
                  <div className='journey-info__field-inner d-flex w-100'>
                    <label className='journey-info__label col-form-label fs-5 fw-bolder text-dark'>
                      {intl.formatMessage({id: 'SHIP_FROM'})}
                    </label>
                    <div className='input-custom journey-info__input'>
                      <InputClickEdit
                        disabled={!isEditableShipFrom}
                        value={shipFromShow}
                        isInvalid={
                          isEditableShipFrom
                            ? getIn(formik.touched, 'ship_from') && checkShipFromValidateFailed
                            : undefined
                        }
                        isValid={
                          isEditableShipFrom
                            ? getIn(formik.touched, 'ship_from') && !checkShipFromValidateFailed
                            : undefined
                        }
                        placeholder={intl.formatMessage({id: 'SHIP_FROM'})}
                        onClick={() => {
                          formik.setFieldTouched('ship_from', true)
                          setFormToModal({
                            show: true,
                            name: 'ship_from',
                            data: formik.getFieldProps('ship_from').value,
                            validate: validateAddress('ship_from'),
                          })
                        }}
                      />
                    </div>
                  </div>
                  {checkShipFromValidateFailed && getIn(formik.touched, 'ship_from') && (
                    <div className='journey-info__field-error'>
                      <span className='text-danger fs-7'>
                        {intl.formatMessage(
                          {id: 'INPUT_IS_REQUIRED'},
                          {input: intl.formatMessage({id: 'SHIP_FROM'})}
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className='journey-info__field journey-info__field--pallets'>
                  <div className='journey-info__field-inner d-flex w-100'>
                    <InputTextFormik
                      className='journey-info__input journey-info__input--compact'
                      label={`# ${intl.formatMessage({id: 'OF_PALLETS'})}`}
                      labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                      formik={formik}
                      type='number'
                      isInteger
                      name='pallets'
                    />
                  </div>
                </div>
                <div className='journey-info__field journey-info__field--parcels'>
                  <div className='journey-info__field-inner d-flex w-100'>
                    <InputTextFormik
                      className='journey-info__input journey-info__input--compact'
                      label={`# ${intl.formatMessage({id: 'OF_PARCELS'})}`}
                      labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                      formik={formik}
                      type='number'
                      isInteger
                      name='parcels'
                    />
                  </div>
                </div>
                <div className='journey-info__field journey-info__field--weight'>
                  <div className='journey-info__field-inner d-flex w-100'>
                    <InputTextFormik
                      className='journey-info__input journey-info__input--compact'
                      label={intl.formatMessage({id: 'KGS'})}
                      labelClassName='journey-info__label col-form-label fs-5 fw-bolder text-dark'
                      formik={formik}
                      type='number'
                      name='weight'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-2'
              label={intl.formatMessage({id: 'CANCEL'})}
              event={handleCloseModal}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'UPDATE'})}
              loadingText={intl.formatMessage({id: 'UPDATE'})}
              disabled={loading.update}
              event={formik.handleSubmit}
              loading={loading.update}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {JourneyInformationModal}
